import { useTranslation } from "react-i18next";
import { Button } from "@components/Button";
import { parseHtml } from "@components/HTMLParser/utils";
import { Checkbox } from "@components/inputs/Checkbox/Checkbox";
import { LargeAccordion } from "@components/LargeAccordion/LargeAccordion";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useAdditionalServiceFilesQuery } from "@services/additional-services/queries";
import {
  DEFAULT_CHUBB_BILL_INSURANCE_SERVICE_PROVIDER_ID,
  type IChubbBillInsuranceJointBillService,
} from "@services/insurance/types";
import { downloadFile, openPdfPreview } from "@utils/common";
import { formatDate } from "@utils/units";

interface IChubbInsuranceModalAccordionProps {
  insuranceData: IChubbBillInsuranceJointBillService;
  isDefaultOpen?: boolean;
  onDeactivationButtonClick: () => void;
  onActivationButtonClick: () => void;
}

export const ChubbInsuranceModalAccordion: React.FC<
  IChubbInsuranceModalAccordionProps
> = ({
  insuranceData,
  isDefaultOpen,
  onDeactivationButtonClick,
  onActivationButtonClick,
}) => {
  const {
    isFakeAddress,
    jointBillServiceId,
    insuranceStatus,
    displayName,
    address,
    firstName,
    lastName,
    dateOfBirth,
    email,
    phoneNumber,
    validTo,
  } = insuranceData;
  const { t } = useTranslation();
  const { toastApiError } = useApiErrorUtils();

  const {
    refetch: downloadAdditionalServiceFiles,
    isLoading: isAdditionalServiceFilesLoading,
  } = useAdditionalServiceFilesQuery(
    DEFAULT_CHUBB_BILL_INSURANCE_SERVICE_PROVIDER_ID,
    jointBillServiceId,
  );

  //TODO: move to some utils or query onSuccess
  const openChubbInsurance = async () => {
    await downloadAdditionalServiceFiles()
      .then((response) => {
        if (response.data?.fileName.endsWith("pdf")) {
          openPdfPreview(response.data.fileName, response.data.data);
        } else if (response.data) {
          downloadFile(response.data);
        }
      })
      .catch(toastApiError);
  };

  return (
    <LargeAccordion
      title={
        <span className="text-sm">
          {displayName ??
            (isFakeAddress ? t("common.defaultAddress") : address)}
        </span>
      }
      isDefaultOpen={isDefaultOpen}
    >
      <>
        {(insuranceStatus === "active" || insuranceStatus === "inactive") && (
          <div className="flex flex-col divide-y divide-grey-light overflow-hidden px-6 text-sm font-medium">
            {/* insuranceType */}
            <div className="flex flex-col justify-between gap-2 py-[18px] md:flex-row">
              <span className="text-grey-dark">
                {t("chubbInsuranceModal.accordion.insuranceType.title")}
              </span>
              <span className="font-semibold">
                {t("chubbInsuranceModal.accordion.insuranceType.value")}
              </span>
            </div>
            {/* insuranceApplicant */}
            <div className="flex flex-col justify-between gap-2 py-[18px] md:flex-row">
              <span className="text-grey-dark">
                {t("chubbInsuranceModal.accordion.insuranceApplicant.title")}
              </span>
              <span className="font-semibold">
                {[firstName, lastName].join(" ")}
              </span>
            </div>
            {/* dateOfBirth */}
            <div className="flex flex-col justify-between gap-2 py-[18px] md:flex-row">
              <span className="text-grey-dark">
                {t("chubbInsuranceModal.accordion.dateOfBirth.title")}
              </span>
              <span className="font-semibold">{formatDate(dateOfBirth)}</span>
            </div>
            {/* email */}
            <div className="flex flex-col justify-between gap-2 py-[18px] md:flex-row">
              <span className="text-grey-dark">
                {t("chubbInsuranceModal.accordion.email.title")}
              </span>
              <span className="font-semibold">{email}</span>
            </div>
            {/* phoneNumber */}
            <div className="flex flex-col justify-between gap-2 py-[18px] md:flex-row">
              <span className="text-grey-dark">
                {t("chubbInsuranceModal.accordion.phoneNumber.title")}
              </span>
              <span className="font-semibold">{phoneNumber}</span>
            </div>
            {/* insuranceValidDate */}
            <div className="flex flex-col justify-between gap-2 py-[18px] md:flex-row">
              <span className="text-grey-dark">
                {t("chubbInsuranceModal.accordion.insuranceValidDate.title")}
              </span>
              <span className="font-semibold">
                {insuranceStatus === "active"
                  ? formatDate(validTo)
                  : t(
                      "chubbInsuranceModal.accordion.insuranceValidDate.inactive",
                    )}
              </span>
            </div>
            {/* insuranceCertificate */}
            {insuranceStatus === "active" && (
              <div className="flex flex-col justify-between gap-2 py-[18px] md:flex-row">
                <span className="text-grey-dark">
                  {t(
                    "chubbInsuranceModal.accordion.insuranceCertificate.title",
                  )}
                </span>
                <Button
                  variant="link"
                  className="font-semibold"
                  onClick={openChubbInsurance}
                  isLoading={isAdditionalServiceFilesLoading}
                >
                  {t(
                    "chubbInsuranceModal.accordion.insuranceCertificate.value",
                  )}
                </Button>
              </div>
            )}
          </div>
        )}
        {/* insuranceTerms footer */}
        <div className="flex flex-col gap-4 border-t border-grey-light p-6 text-sm text-grey-dark">
          {insuranceStatus === "active" || insuranceStatus === "inactive" ? (
            <>
              <span>
                {t("chubbInsuranceModal.accordion.insuranceTerms.title")}
              </span>
              <Checkbox
                label={parseHtml(
                  t("chubbInsuranceModal.accordion.billInsuranceTerms.label", {
                    link1: t(
                      "billInsuranceActivationModal.form.fields.billInsuranceTerms.link1",
                    ),
                    link2: t(
                      "billInsuranceActivationModal.form.fields.billInsuranceTerms.link2",
                    ),
                  }),
                )}
                checked
                disabled
              />
              <Checkbox
                label={parseHtml(
                  t(
                    "chubbInsuranceModal.accordion.dataTransferToBillInsurer.label",
                    {
                      link: t(
                        "billInsuranceActivationModal.form.fields.dataTransferToBillInsurer.link",
                      ),
                    },
                  ),
                )}
                checked
                disabled
              />
              <Button variant="secondary" onClick={onDeactivationButtonClick}>
                {insuranceStatus === "active"
                  ? t("chubbInsuranceModal.accordion.actions.deactivate")
                  : t(
                      "chubbInsuranceModal.accordion.actions.withdrawApplication",
                    )}
              </Button>
            </>
          ) : (
            <>
              <span>
                {parseHtml(t("chubbInsuranceModal.accordion.activation.title"))}
              </span>
              <Button onClick={onActivationButtonClick}>
                {t("chubbInsuranceModal.accordion.actions.activate")}
              </Button>
            </>
          )}
        </div>
      </>
    </LargeAccordion>
  );
};
